import React, { createContext, useState, useContext, useEffect, useTransition } from 'react';
import { jwtDecode } from 'jwt-decode';
import { AuthAPI, api, setForceLogoutHandler } from '../utils/api';
import axios from 'axios';
import { Outlet } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isPending, startTransition] = useTransition();

  // Helper to check if user needs org onboarding
  const checkOnboarding = (userData) => {
    if (!userData) return false;
    // Clients and students don't need org onboarding (students get a personal
    // study space provisioned by the backend, not an organization).
    if (userData.role === 'client' || userData.role === 'student') return false;
    // Dismissed onboarding in this session — skip regardless
    if (localStorage.getItem('onboarding_dismissed')) return false;
    // User has no organization — needs to set one up
    if (!userData.organization) return true;
    return false;
  };

  const completeOnboarding = () => {
    setNeedsOnboarding(false);
    // Refresh user data
    AuthAPI.getCurrentUser().then(res => {
      const userData = {
        ...(res.data || res),
        role: (res.data || res).role || (res.data || res).user_role || 'user',
      };
      setUser(userData);
    }).catch(() => {});
  };

  const dismissOnboarding = () => {
    localStorage.setItem('onboarding_dismissed', 'true');
    setNeedsOnboarding(false);
  };

  const showOnboarding = () => {
    setNeedsOnboarding(true);
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (token) {
        try {
          // Set the token in the API headers
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Verify token is valid
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decodedToken.exp > currentTime) {
            try {
              // Token is valid, fetch current user info
              const userResponse = await AuthAPI.getCurrentUser();
              const userInfo = userResponse.data || userResponse;
              // Ensure role is properly set from the user info
              const userData = {
                ...userInfo,
                role: userInfo.role || userInfo.user_role || (userInfo.is_admin ? 'admin' : 'user')
              };

              startTransition(() => {
                setUser(userData);
                setIsAuthenticated(true);
                setAccessToken(token);
                setNeedsOnboarding(checkOnboarding(userData));
                setIsLoading(false);
              });
            } catch (error) {
              // Error fetching user info - try to refresh token
              console.error('Error fetching user data:', error);
              if (refreshToken) {
                try {
                  const refreshResponse = await AuthAPI.refreshToken(refreshToken);
                  const newToken = refreshResponse.access_token || refreshResponse.access;
                  const newRefresh = refreshResponse.refresh_token || refreshResponse.refresh;

                  localStorage.setItem('accessToken', newToken);
                  if (newRefresh) {
                    localStorage.setItem('refreshToken', newRefresh);
                  }
                  api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                  setAccessToken(newToken);

                  // Try fetching user again
                  const userResponse = await AuthAPI.getCurrentUser();
                  const userInfo = userResponse.data || userResponse;
                  const userData = {
                    ...userInfo,
                    role: userInfo.role || userInfo.user_role || (userInfo.is_admin ? 'admin' : 'user')
                  };

                  startTransition(() => {
                    setUser(userData);
                    setIsAuthenticated(true);
                    setIsLoading(false);
                  });
                } catch (refreshError) {
                  console.error('Token refresh failed:', refreshError);
                  // Clear all tokens and reset state
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');
                  delete api.defaults.headers.common['Authorization'];
                  startTransition(() => {
                    setUser(null);
                    setIsAuthenticated(false);
                    setAccessToken(null);
                    setIsLoading(false);
                  });
                }
              } else {
                // No refresh token available
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                delete api.defaults.headers.common['Authorization'];
                startTransition(() => {
                  setUser(null);
                  setIsAuthenticated(false);
                  setAccessToken(null);
                  setIsLoading(false);
                });
              }
            }
          } else {
            // Token expired - try to refresh
            if (refreshToken) {
              try {
                const refreshResponse = await AuthAPI.refreshToken(refreshToken);
                const newToken = refreshResponse.access_token || refreshResponse.access;
                const newRefresh = refreshResponse.refresh_token || refreshResponse.refresh;

                localStorage.setItem('accessToken', newToken);
                if (newRefresh) {
                  localStorage.setItem('refreshToken', newRefresh);
                }
                api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                setAccessToken(newToken);

                // Fetch user info with new token
                const userResponse = await AuthAPI.getCurrentUser();
                const userInfo = userResponse.data || userResponse;
                const userData = {
                  ...userInfo,
                  role: userInfo.role || userInfo.user_role || (userInfo.is_admin ? 'admin' : 'user')
                };

                startTransition(() => {
                  setUser(userData);
                  setIsAuthenticated(true);
                  setIsLoading(false);
                });
              } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                // Clear all tokens
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                delete api.defaults.headers.common['Authorization'];
                startTransition(() => {
                  setUser(null);
                  setIsAuthenticated(false);
                  setAccessToken(null);
                  setIsLoading(false);
                });
              }
            } else {
              // No refresh token - clear expired token
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              delete api.defaults.headers.common['Authorization'];
              startTransition(() => {
                setUser(null);
                setIsAuthenticated(false);
                setAccessToken(null);
                setIsLoading(false);
              });
            }
          }
        } catch (error) {
          console.error('Error initializing auth:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
          setAccessToken(null);
          setIsLoading(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setAccessToken(null);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async ({ email, password }) => {
    try {
      setIsLoading(true);
      console.log("Attempting login for:", email);

      const response = await AuthAPI.login({ email, password });
      console.log("Login response received:", response);

      const data = response.data || response;
      console.log("Login data:", data);

      // Check if 2FA is required
      if (data.requires_2fa) {
        console.log("2FA required for:", email);
        // Return the 2FA requirement to the login page for handling
        return { requires_2fa: true, email, password };
      }

      const token = data.access_token || data.access || data.token;
      if (!token) {
        console.error('Login response data:', data);
        throw new Error('No access token received from server');
      }

      console.log("Access token extracted successfully");

      // Store tokens
      localStorage.setItem('accessToken', token);
      if (data.refresh) {
        localStorage.setItem('refreshToken', data.refresh);
      }

      // Set the token in axios headers immediately
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAccessToken(token);

      // Handle user data from login response
      let userData = data.user || data;

      // If we have user data from login response, use it
      if (userData && userData.email) {
        userData = {
          ...userData,
          role: userData.role || userData.user_role || (userData.is_admin ? 'admin' : 'user')
        };

        setUser(userData);
        setIsAuthenticated(true);
        setNeedsOnboarding(checkOnboarding(userData));
      } else {
        // Fallback: fetch user info if not included in login response
        console.log('User data not in login response, fetching from /me endpoint');
        try {
          const userResponse = await AuthAPI.getCurrentUser();
          userData = {
            ...userResponse.data,
            role: userResponse.data.role || userResponse.data.user_role || (userResponse.data.is_admin ? 'admin' : 'user')
          };
          setUser(userData);
          setIsAuthenticated(true);
          setNeedsOnboarding(checkOnboarding(userData));
        } catch (err) {
          console.error('Failed to fetch user info after login:', err);
          // Still set as authenticated since login was successful
          setUser({ email });
          setIsAuthenticated(true);
        }
      }

      // Check if password change is required
      if (data.must_change_password) {
        setMustChangePassword(true);
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const complete2FALogin = async (responseData) => {
    /**
     * Complete the login after successful 2FA verification.
     * Accepts the response data from the verify-2fa endpoint.
     */
    try {
      setIsLoading(true);
      const data = responseData.data || responseData;

      const token = data.access_token || data.access || data.token;
      if (!token) {
        throw new Error('No access token received after 2FA verification');
      }

      // Store tokens
      localStorage.setItem('accessToken', token);
      if (data.refresh) {
        localStorage.setItem('refreshToken', data.refresh);
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAccessToken(token);

      let userData = data.user || data;
      if (userData && userData.email) {
        userData = {
          ...userData,
          role: userData.role || userData.user_role || (userData.is_admin ? 'admin' : 'user')
        };
        setUser(userData);
        setIsAuthenticated(true);
        setNeedsOnboarding(checkOnboarding(userData));
      } else {
        try {
          const userResponse = await AuthAPI.getCurrentUser();
          userData = {
            ...userResponse.data,
            role: userResponse.data.role || userResponse.data.user_role || (userResponse.data.is_admin ? 'admin' : 'user')
          };
          setUser(userData);
          setIsAuthenticated(true);
          setNeedsOnboarding(checkOnboarding(userData));
        } catch (err) {
          console.error('Failed to fetch user info after 2FA:', err);
          setUser({ email: data.email });
          setIsAuthenticated(true);
        }
      }

      // Check if password change is required
      if (data.must_change_password) {
        setMustChangePassword(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('onboarding_dismissed');
    delete api.defaults.headers.common['Authorization'];
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setNeedsOnboarding(false);
    setMustChangePassword(false);
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
  };

  // Register the force-logout handler so the Axios interceptor can
  // update React state when a token refresh fails (session expired).
  useEffect(() => {
    setForceLogoutHandler(() => {
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setNeedsOnboarding(false);
    });
    return () => setForceLogoutHandler(null);
  }, []);

  const register = async (userData) => {
    try {
      setIsLoading(true);

      const dataToSend = { ...userData };
      if (!dataToSend.password2 && dataToSend.password) {
        dataToSend.password2 = dataToSend.password;
      }

      console.log("Sending registration data to API:", dataToSend);

      // Empty string fallback for nginx proxy in Docker
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const apiUrl = `${baseUrl}/api/accounts/register/`;

      console.log("Registration API URL:", apiUrl);

      const response = await axios.post(
        apiUrl,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      console.log("Registration successful:", response.data);

      return response.data;
    } catch (error) {
      console.error('Registration error:', error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          console.error('API Error Response:', errorData);
        }
        if (errorData.detail) {
          throw errorData;
        }
      }

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate isAdmin value
  const isAdmin = user && (user.role === 'admin' || user.is_admin === true);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    accessToken,
    login,
    logout,
    register,
    isAdmin,
    needsOnboarding,
    completeOnboarding,
    dismissOnboarding,
    showOnboarding,
    complete2FALogin,
    mustChangePassword,
    clearMustChangePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children || <Outlet />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
